import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { DeleteOutlined, DragIndicatorOutlined } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import {
  CanvasTablePreviewCard,
  DeleteButton,
  DragHandle,
  CanvasItemIcon,
  TablePreviewBorder,
  TablePreviewTable,
  TablePreviewTh,
  TablePreviewTd,
} from './DesignerCanvasSection.styles'
import { ITableComponentInstance, DEFAULT_TABLE_STYLES } from 'src/types/report-designer'

export interface ICanvasTableItemProps {
  item: ITableComponentInstance
  componentIcon: React.ReactNode
  isSelected: boolean
  accentColor: string
  isDragging: boolean
  isDragOver: boolean
  onSelectItem: (id: string) => void
  onDeleteItem: (id: string) => void
  onDragStart: (e: React.DragEvent) => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent) => void
  onDragEnd: () => void
}

export const CanvasTableItem: React.FC<ICanvasTableItemProps> = ({
  item,
  componentIcon,
  isSelected,
  accentColor,
  isDragging,
  isDragOver,
  onSelectItem,
  onDeleteItem,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) => {
  const { t: translate } = useTranslation(['dynamic-report'])
  const theme = useTheme()

  return (
    <CanvasTablePreviewCard
      key={item.id}
      isSelected={isSelected}
      accentColor={accentColor}
      isDragging={isDragging}
      isDragOver={isDragOver}
      onClick={() => onSelectItem(item.id)}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DragHandle>
          <DragIndicatorOutlined fontSize="small" />
        </DragHandle>
        <CanvasItemIcon accentColor={accentColor}>{componentIcon}</CanvasItemIcon>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" fontWeight={600}>
            {translate('data-table', { ns: 'dynamic-report' })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {item.columns.length} {translate('columns', { ns: 'dynamic-report' })}
          </Typography>
        </Box>
        <DeleteButton
          size="small"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation()
            onDeleteItem(item.id)
          }}
          title={translate('delete-item', { ns: 'dynamic-report' })}
        >
          <DeleteOutlined fontSize="small" />
        </DeleteButton>
      </Box>

      {/* Table preview */}
      {item.columns.length > 0 ? (
        <TablePreviewBorder>
          <TablePreviewTable
            style={{ fontSize: `${item.tableStyles?.fontSize ?? DEFAULT_TABLE_STYLES(theme).fontSize}px` }}
          >
            <thead>
              <tr>
                {item.columns.map(col => (
                  <TablePreviewTh
                    key={col.id}
                    accentColor={accentColor}
                    style={{
                      ...{ width: `${col.width ?? 100}px`, minWidth: `${col.width ?? 100}px` },
                      color: item.tableStyles?.headerColor ?? DEFAULT_TABLE_STYLES(theme).headerColor,
                      backgroundColor:
                        item.tableStyles?.headerBackgroundColor ?? DEFAULT_TABLE_STYLES(theme).headerBackgroundColor,
                    }}
                  >
                    {col.fieldName}
                  </TablePreviewTh>
                ))}
              </tr>
            </thead>
            <tbody>
              {[0, 1, 2, 3].map(rowIdx => (
                <tr
                  key={rowIdx}
                  style={{
                    height: `${item.tableStyles?.rowHeight ?? DEFAULT_TABLE_STYLES(theme).rowHeight}px`,
                  }}
                >
                  {item.columns.map(col => (
                    <TablePreviewTd key={col.id}>Data</TablePreviewTd>
                  ))}
                </tr>
              ))}
            </tbody>
          </TablePreviewTable>
        </TablePreviewBorder>
      ) : (
        <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', pl: 0.5 }}>
          {translate('no-columns', { ns: 'dynamic-report' })}
        </Typography>
      )}
    </CanvasTablePreviewCard>
  )
}
